import { ApiResponseError, ApiValidationError } from "@faro-lotv/service-wires";
import {
  isApiError,
  isSavedView,
  isSavedViewCreated,
  type ApiError,
  type SavedView,
  type SavedViewBody,
} from "@faro-lotv/sphere-viewer-contracts";

/**
 * Creates a saved view on the backend and returns its share id.
 *
 * @param body The saved view payload to persist.
 * @returns the id of the created saved view, used to build `/share/<id>` links.
 * @throws {ApiResponseError} if the api rejects the request (e.g. `400` for an
 *   invalid body, `413` for an oversized one).
 * @throws {ApiValidationError} if the response does not contain a valid id.
 */
export async function createSavedView(body: SavedViewBody): Promise<string> {
  const response = await fetch("/api/savedView", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await throwResponseError(response);
  }

  const payload: unknown = await readJsonBody(response);
  if (!isSavedViewCreated(payload)) {
    throw new ApiValidationError(payload);
  }

  return payload.id;
}

/**
 * Fetches a saved view from the backend by its share id.
 *
 * @param id The short, URL-safe id from a `/share/<id>` link.
 * @returns the persisted saved view, including its `projectId` and `data`.
 * @throws {ApiResponseError} if the api rejects the request (`400` for a
 *   malformed id, `404` if no view exists for it).
 * @throws {ApiValidationError} if the response is not a valid saved view.
 */
export async function getSavedView(id: string): Promise<SavedView> {
  const response = await fetch(`/api/savedView/${encodeURIComponent(id)}`);

  if (!response.ok) {
    await throwResponseError(response);
  }

  const payload: unknown = await readJsonBody(response);
  if (!isSavedView(payload)) {
    throw new ApiValidationError(payload);
  }

  return payload;
}

/**
 * Throws the repo-standard error for a non-OK saved-views API response.
 *
 * The api reports failures as an {@link ApiError} JSON body; when present it is
 * attached to the thrown {@link ApiResponseError} so the server's message
 * survives into error reporting. Callers can branch on specific failures via
 * `error instanceof ApiResponseError && error.status === 404`.
 *
 * @param response The non-OK response returned by the api.
 */
async function throwResponseError(response: Response): Promise<never> {
  const payload: unknown = await readJsonBody(response);

  throw new ApiResponseError<number, ApiError>(
    response.status,
    response.statusText,
    isApiError(payload) ? payload : undefined,
  );
}

/**
 * Reads a response body as JSON, treating an unparseable body as absent.
 *
 * A response can carry a body we cannot parse whatever its status says: an
 * empty body, or the SPA fallback page Static Web Apps serves when `/api` does
 * not reach the Functions host. Every caller feeds the result to a contract
 * guard, so `undefined` is rejected as an invalid payload like any other
 * mismatch, instead of surfacing a raw `SyntaxError` from `Response.json()`.
 *
 * @param response The response to read.
 * @returns The parsed body, or `undefined` if it is not valid JSON.
 */
async function readJsonBody(response: Response): Promise<unknown> {
  return await response.json().catch(() => undefined);
}
